import { nextTick } from 'vue';

import { useModalStore } from '@/stores/components/modalStore';
import { useTrackingStore } from '@/stores/tracking/trackingStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { useIdentityStore } from '@/stores/identityStore';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';
import usePaymentFilters from '@/composables/filters/usePaymentFilters';
import useErrorRedirect from '@/composables/api/useErrorRedirect';

// Error codes that should redirect the user to the generic error page.
const SWITCH_PSP_REDIRECT_ERROR_CODES = [
  5052, // Order is not switchable
  5055, // Order is not switchable due to its status
  5057, // Order state changed during PSP switch attempt
  5058, // Order does not belong to the authenticated operator
];

// Error codes that should be shown as a toast on the current payment page.
const SWITCH_PSP_TOAST_ERROR_CODES = [
  5054, // Sell orders cannot be switched
  5056, // Order is not switchable due to existing PSP side effects
  4007, // Payment type is not available
  4015, // payment provider error
];

export default ({ router: providedRouter } = {}) => {
  const toggleActionModal = (
    toShow,
    data = {
      title: '',
      text: '',
      primaryBtnText: null,
      primaryBtnAction: null,
      secondaryBtnText: null,
      secondaryBtnAction: null,
    },
    alignment = {
      'justify-content': 'center',
      'align-content': 'center',
    },
  ) => {
    const store = useModalStore();
    store.$state.actionModal.toShow = toShow;
    store.$state.actionModal.options = data;
    store.$state.actionModal.options.alignment = alignment;
  };

  const showWalletModal = (
    data = {
      title: '',
      text: '',
      primaryBtnText: null,
      primaryBtnAction: null,
      secondaryBtnText: null,
      secondaryBtnAction: null,
      alignment: {},
    },
  ) => {
    const store = useModalStore();
    store.$state.walletModal.toShow = true;
    store.$state.walletModal.options = data;
  };

  const hideWalletModal = () => {
    const store = useModalStore();
    store.toggleWalletModal(false);
  };

  const toggleDofpFailureModal = (
    toShow,
    opts = {
      body: '',
    },
    alignment = {
      'justify-content': 'center',
      'align-content': 'center',
    },
  ) => {
    const store = useModalStore();
    store.$state.dofpFailureModal.toShow = toShow;
    store.$state.dofpFailureModal.options.body = opts?.body;
    store.$state.dofpFailureModal.options.alignment = alignment;
  };

  const toggleDofpNotifyModal = (
    toShow,
    type = 'notify',
    opts = {
      title: '',
      body: '',
      primaryBtnText: null,
      primaryBtnAction: null,
      secondaryBtnText: null,
      secondaryBtnAction: null,
      alignment: {
        'justify-content': 'center',
        'align-content': 'center',
      },
    },
  ) => {
    const store = useModalStore();
    store.dofpNotifyModal.toShow = toShow;
    store.dofpNotifyModal.type = type;
    store.dofpNotifyModal.options = {
      ...store.dofpNotifyModal.options,
      ...opts,
    };
  };

  const toggleFormComponentModal = (
    toShow,
    componentPath,
    btnActions = {
      primaryBtnAction: () => toggleFormComponentModal(false),
      secondaryBtnAction: null,
    },
    alignment = {
      overflow: 'scroll',
      'justify-content': 'center',
      'align-content': 'start',
    },
  ) => {
    const store = useModalStore();

    store.$state.formComponentModal.toShow = toShow;
    store.$state.formComponentModal.options.componentPath = componentPath;
    store.$state.formComponentModal.options.primaryBtnAction = btnActions.primaryBtnAction;
    store.$state.formComponentModal.options.secondaryBtnAction = btnActions.secondaryBtnAction;
    store.$state.formComponentModal.options.alignment = alignment;
  };

  const toggleOrderRecoveryModal = (option = null) => {
    const store = useModalStore();
    store.orderRecoveryModal.toShow = option !== null ? option : !store.orderRecoveryModal.toShow;
  };

  const togglePaymentAttemptModal = (
    toShow,
    opts = {},
    alignment = {
      'justify-content': 'center',
      'align-content': 'center',
    },
  ) => {
    const store = useModalStore();
    store.$state.paymentAttemptModal.toShow = toShow;
    store.$state.paymentAttemptModal.options = {
      ...store.$state.paymentAttemptModal.options,
      ...opts,
      alignment,
    };
  };

  const toggleRedirectModal = (toShow = null, options = {}) => {
    const store = useModalStore();
    store.redirectModal.toShow = toShow !== null ? toShow : !store.redirectModal.toShow;
    store.redirectModal.options = { ...store.redirectModal.options, ...options };
  };

  const PAYMENT_SWITCH_LOADER_DURATION_MS = 1600;

  const showPaymentSwitchLoader = (openNext) => {
    const store = useModalStore();
    store.setPaymentSwitchLoading(true);
    setTimeout(() => {
      store.setPaymentSwitchLoading(false);
      openNext();
    }, PAYMENT_SWITCH_LOADER_DURATION_MS);
  };

  const togglePaymentFailureModal = (toShow = null, opts = {}) => {
    const store = useModalStore();
    const nextValue = toShow !== null ? toShow : !store.paymentFailureModal.toShow;
    store.togglePaymentFailureModal(nextValue, opts);
  };

  const handlePaymentFailureSwitchMethod = () => {
    const store = useModalStore();
    const { onSwitchMethod } = store.paymentFailureModal.options;
    togglePaymentFailureModal(false);
    showPaymentSwitchLoader(() => {
      if (onSwitchMethod) {
        onSwitchMethod();
      }
    });
  };

  const handlePaymentFailureTryAgain = () => {
    const store = useModalStore();
    togglePaymentFailureModal(false);
    if (store.paymentFailureModal.options.onTryAgain) {
      store.paymentFailureModal.options.onTryAgain();
    }
  };

  const togglePaymentKycModal = (toShow, opts = {}) => {
    const store = useModalStore();
    store.togglePaymentKycModal(toShow, opts);
  };

  const handlePaymentKycSubmitted = () => {
    const store = useModalStore();
    const { onSubmit } = store.paymentKycModal.options;
    store.togglePaymentKycModal(false);
    onSubmit?.();
  };

  const togglePaymentMethodSwitchModal = (toShow = null, opts = {}) => {
    const store = useModalStore();
    const nextValue = toShow !== null ? toShow : !store.paymentMethodSwitchModal.toShow;
    const resolvedOpts = { ...opts };
    if (nextValue && !resolvedOpts.selectedPaymentCode && resolvedOpts.paymentMethods?.length) {
      resolvedOpts.selectedPaymentCode = resolvedOpts.paymentMethods[0].code;
    }
    store.togglePaymentMethodSwitchModal(nextValue, resolvedOpts);
  };

  const handlePaymentKycGoBack = () => {
    const store = useModalStore();
    store.togglePaymentKycModal(false);
    showPaymentSwitchLoader(() => {
      togglePaymentMethodSwitchModal(true);
    });
  };

  const handlePaymentMethodSelect = (code) => {
    const store = useModalStore();
    store.setPaymentMethodSwitchSelectedCode(code);
    if (store.paymentMethodSwitchModal.options.onSelect) {
      store.paymentMethodSwitchModal.options.onSelect(code);
    }
  };

  const handlePaymentMethodContinue = async (code) => {
    const store = useModalStore();
    const trackingStore = useTrackingStore();
    const orderStore = useOrderFormStore();
    const { redirectToErrorPage } = useErrorRedirect(providedRouter);
    const { switchPsp, getSwitchablePsps } = useOrderEndpoints();
    const { removeApplepayByUserSession } = usePaymentFilters();

    store.togglePaymentMethodSwitchModal(false);
    store.setPaymentSwitchLoading(true);

    const handleSwitchPspError = async ({ errorCode, errorMessage }) => {
      store.setPaymentSwitchLoading(false);
      await nextTick();

      const normalizedErrorCode = Number(errorCode);

      if (normalizedErrorCode === 242) {
        const identityStore = useIdentityStore();
        const nextRoute = await identityStore.fetchNextIdentityRoute(
          providedRouter?.currentRoute?.value,
        );
        if (nextRoute) {
          await providedRouter?.push?.(nextRoute);
        }
        return;
      }

      if (SWITCH_PSP_REDIRECT_ERROR_CODES.includes(normalizedErrorCode)) {
        await redirectToErrorPage({ code: errorCode, message: errorMessage });
      } else if (SWITCH_PSP_TOAST_ERROR_CODES.includes(normalizedErrorCode)) {
        togglePaymentFailureModal(true, {
          errorCode: String(errorCode),
          errorMessage,
          onSwitchMethod: () => togglePaymentMethodSwitchModal(true),
          onTryAgain: () => handlePaymentMethodContinue(code),
        });
      } else {
        await redirectToErrorPage({
          code: errorCode || 'switch-psp-error',
          message: errorMessage || 'An unexpected error occurred while switching payment method.',
        });
      }
    };

    const orderKey = trackingStore.getOrderKey;

    try {
      if (!orderKey || !code) {
        return;
      }

      const response = await switchPsp(orderKey, code);

      // Refresh the list of switchable PSPs after a successful switch so the next
      // payment form knows whether to show the switch button.
      try {
        const switchableResponse = await getSwitchablePsps(orderKey);
        const raw = removeApplepayByUserSession(
          switchableResponse?.data?.data?.payment_methods
            ?? switchableResponse?.data?.payment_methods
            ?? [],
        );
        orderStore.setSwitchablePaymentMethods(raw.map((pm) => ({
          code: pm.code,
          name: pm.name,
          paymentType: pm.paymentType,
          fee: 0,
          transaction_fees: pm.transaction_fees ?? [],
          transactionLimits: pm.transactionLimits ?? null,
        })));
      } catch {
        orderStore.clearSwitchablePaymentMethods();
      }

      const errorCode = response?.data?.data?.code
        ?? response?.data?.code
        ?? response?.code;
      const errorMessage = response?.data?.data?.message
        ?? response?.data?.message
        ?? response?.message;
      const order = response?.data?.order ?? response?.data?.data?.order;
      const paymentMethods = store.paymentMethodSwitchModal.options.paymentMethods ?? [];
      const selectedMethod = paymentMethods.find((pm) => pm.code === code);

      // Prefer the user's selected payment method, since the switch-psp response order
      // may not always contain the new payment code / type in the expected fields.
      const paymentCode = selectedMethod?.code
        ?? code;
      const paymentType = selectedMethod?.paymentType
        ?? order?.paymentType
        ?? order?.paymentProviderType
        ?? order?.pspTypeCode
        ?? paymentCode;

      // When the switch succeeds or is blocked by KYC (242), the user already
      // selected a payment method. Persist it so the payment form renders the
      // correct component after KYC / navigation.
      if (!errorCode || Number(errorCode) === 242) {
        if (paymentCode) {
          orderStore.setActiveModePaymentProvider(paymentCode);
        }
        if (paymentType) {
          orderStore.setPspTypeCode(paymentType.toLowerCase());
        }

        if (order) {
          if (order.currencyValue !== undefined) {
            orderStore.updateOrderAccordion('fiatAmount', order.currencyValue);
          }
          if (order.coinValue !== undefined) {
            orderStore.updateOrderAccordion('coinAmount', order.coinValue);
          }
          if (order.paymentFee !== undefined || order.fee !== undefined) {
            orderStore.updateOrderAccordion('gatewayFee', order.paymentFee ?? order.fee);
          }
          if (order.networkFee !== undefined) {
            orderStore.updateOrderAccordion('networkFee', order.networkFee);
          }
        }
      }

      if (errorCode) {
        await handleSwitchPspError({ errorCode, errorMessage });
        return;
      }

      if (paymentCode) {
        await providedRouter?.push?.({
          name: 'payment.payment',
          params: { payment: paymentCode.toLowerCase() },
        });
      }
    } catch (err) {
      const failure = err?.response?.data ?? err?.data ?? err;
      const errorCode = failure?.code ?? err?.code;
      const errorMessage = failure?.message ?? err?.message ?? '';
      await handleSwitchPspError({
        errorCode,
        errorMessage,
      });
    } finally {
      setTimeout(() => {
        store.setPaymentSwitchLoading(false);
      }, 800);
    }

    if (store.paymentMethodSwitchModal.options.onContinue) {
      store.paymentMethodSwitchModal.options.onContinue(code);
    }
  };

  const handlePaymentMethodGoBack = () => {
    const store = useModalStore();
    togglePaymentMethodSwitchModal(false);
    if (store.paymentMethodSwitchModal.options.onGoBack) {
      store.paymentMethodSwitchModal.options.onGoBack();
    }
  };

  return {
    toggleActionModal,
    showWalletModal,
    hideWalletModal,
    toggleFormComponentModal,
    toggleDofpFailureModal,
    toggleDofpNotifyModal,
    toggleOrderRecoveryModal,
    toggleRedirectModal,
    togglePaymentAttemptModal,
    showPaymentSwitchLoader,
    togglePaymentFailureModal,
    handlePaymentFailureSwitchMethod,
    handlePaymentFailureTryAgain,
    togglePaymentKycModal,
    handlePaymentKycSubmitted,
    handlePaymentKycGoBack,
    togglePaymentMethodSwitchModal,
    handlePaymentMethodSelect,
    handlePaymentMethodContinue,
    handlePaymentMethodGoBack,
  };
};
